/* eslint-disable camelcase */
import { createContext } from 'react';

import type { TrackingContextData } from 'js/lib/retracked';

// accepted 'apps' and 'pages' for heartbeat event tracking; used for Retracked context data
export type HeartbeatApp = '' | 'course';
export type HeartbeatPage =
  | ''
  | 'gdp_assignment_grading'
  | 'gdp_submission_grading'
  | 'gradebook_manager_advanced'
  | 'gradebook_manager_default'
  | 'grading_overview'
  | 'peer_review_assignment_grading'
  | 'peer_review_submission_grading'
  | 'sga_assignment_grading'
  | 'sga_submission_grading'
  | 'tmr_assignment_grading'
  | 'tmr_submission_grading';

// accepted list of default context data used for Retracked for each page
export const HeartbeatContextData: Record<HeartbeatPage, TrackingContextData & HeartbeatContextNamespace> = {
  gdp_assignment_grading: {
    namespace: { app: 'course', page: 'gdp_assignment_grading' },
  },
  gdp_submission_grading: {
    namespace: { app: 'course', page: 'gdp_submission_grading' },
  },
  gradebook_manager_advanced: {
    namespace: { app: 'course', page: 'gradebook_manager_advanced' },
  },
  gradebook_manager_default: {
    namespace: { app: 'course', page: 'gradebook_manager_default' },
  },
  grading_overview: {
    namespace: { app: 'course', page: 'grading_overview' },
  },
  peer_review_assignment_grading: {
    namespace: { app: 'course', page: 'peer_review_assignment_grading' },
  },
  peer_review_submission_grading: {
    namespace: { app: 'course', page: 'peer_review_submission_grading' },
  },
  sga_assignment_grading: {
    namespace: { app: 'course', page: 'sga_assignment_grading' },
  },
  sga_submission_grading: {
    namespace: { app: 'course', page: 'sga_submission_grading' },
  },
  tmr_assignment_grading: {
    namespace: { app: 'course', page: 'tmr_assignment_grading' },
  },
  tmr_submission_grading: {
    namespace: { app: 'course', page: 'tmr_submission_grading' },
  },
  '': {
    namespace: { app: '', page: '' },
  },
};

export const HeartbeatDefaults = {
  DEFAULT_INTERVAL: 10000, // 10 seconds.
  DEFAULT_VERSION: 'v1',
  DEFAULT_ACTION: 'heartbeat',
};

export type HeartbeatData = {
  page: HeartbeatPage;
  routeNames: string[]; // should use the RouteNames defined in 'pages/teach-course/constants'
  version?: string;
  eventData?: Record<string, string | number | undefined>;
};

type HeartbeatContextNamespace = {
  namespace: {
    app: HeartbeatApp;
    page: HeartbeatPage;
  };
};

const noop = () => {
  // Do nothing
};

export type HeartbeatContextType = {
  setHeartbeatData: (newHeartbeatData?: HeartbeatData) => void;
  addConsumer: () => void;
  removeConsumer: () => void;
};

export const HeartbeatContext = createContext<HeartbeatContextType>({
  setHeartbeatData: noop,
  addConsumer: noop,
  removeConsumer: noop,
});
